import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { colors } from '../../../../colors';
import { ComponentDimensions } from '../../../../types/Layout';
import useCPCDaemonQueueWebsocket from '../../../../hooks/useCPCDaemonQueueWebsocket';
import useCPCProgressWebsocket from '../../../../hooks/useCPCProgressWebsocket';
import useRequest from '../../../../hooks/useRequest';
import ServicePaths from '../../../../bridge/utils/ServicePaths';
import SpcResultView from '../../../Subscription/Content/Calculations/SpcResultView';
import ProfitDistributionChart from '../CalculationResultHistory/ProfitDistributionChart';
import {
  Bar,
  Panel,
  QueueListItem,
  StatsBlock,
  InterruptedBanner,
  GlobalBestCard,
  formatDurationCompact,
} from './shared';
import { computeComboETAMs, computeOverallETAMs } from './eta';

const MOBILE_MAX_WIDTH = 700;
const TABLET_MAX_WIDTH = 1100;

const reorderQueueOnServer = (botFriendlyName: string): void => {
  fetch(ServicePaths.DATA.ReorderDaemonQueue, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ botFriendlyName }),
  }).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('Failed to reorder daemon queue', err);
  });
};

interface Props {
  rootDimensions?: ComponentDimensions | undefined;
}

const LiveCPCDaemon = ({ rootDimensions }: Props) => {
  const queue = useCPCDaemonQueueWebsocket();
  const progress = useCPCProgressWebsocket();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // The parent measures `rootDimensions` once at mount and never updates it on
  // resize, so we observe our own root element to drive responsive breakpoints.
  // The dep on isIdle re-binds the observer after the early-return path swaps
  // out for the full layout, when rootRef.current finally points at the real
  // root.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setMeasuredWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [progress.isIdle]);

  const comboETA =
    progress.phase && progress.currentCombo
      ? computeComboETAMs({
          nowMs: now,
          comboStartedAt: progress.currentCombo.startedAt,
          phase: progress.phase,
          phaseDone: progress.phaseDone,
          phaseTotal: progress.phaseTotalIterations,
          passIndex: progress.phasePass,
        })
      : null;

  const fallbackTotalDurationMs =
    queue.queueState?.queue.find((q) => q.active)?.durationCPC ?? null;

  const overallETA = computeOverallETAMs({
    comboIndex: progress.comboIndex,
    comboTotal: progress.comboTotal,
    finishedDurations: progress.finishedDurations,
    fallbackTotalDurationMs,
    currentComboETAMs: comboETA,
  });

  const spcParams = useMemo(() => {
    const best = progress.bestGlobal;
    if (!best) return undefined;
    return {
      baseAsset: best.params.baseAsset,
      quoteAsset: best.params.quoteAsset,
      exchange: best.params.exchange,
      startTimestamp: new Date(best.params.startTimestamp).valueOf(),
      endTimestamp: Date.now(),
      higherTimeFrame: best.params.higherTimeFrame,
      shorterTimeFrame: best.params.shorterTimeFrame,
      multiOrderTimeFrame: best.params.multiOrderTimeFrame,
      cbrt: best.params.cbrt,
      shorterTimeFrameInterval: best.params.shorterTimeFrameInterval,
      supportCbrtRange: best.params.supportCbrtRange,
      supportMrcState: best.params.supportMrcState,
      useTakeProfitTransactions: !!best.indicatorConfig,
      indicatorConfig: best.indicatorConfig,
    };
  }, [progress.bestGlobal]);

  const spcOptions = useMemo(
    () => ({ method: 'POST' as const, shouldFetch: !!spcParams }),
    [spcParams],
  );

  const { data: spcResult } = useRequest(
    ServicePaths.PROFIT_CALCULATIONS.GetSingleProfitCalculation,
    spcParams,
    spcOptions,
  );

  const handleReorder = useCallback((botFriendlyName: string) => {
    reorderQueueOnServer(botFriendlyName);
  }, []);

  if (progress.isIdle) {
    return (
      <div
        style={{
          display: 'grid',
          placeItems: 'center',
          height: '100%',
          color: colors.gray,
        }}
      >
        Daemon idle — trigger a CPC via <code>POST /calculations/add</code> to
        see live updates.
      </div>
    );
  }

  const overallPct =
    progress.comboTotal === 0
      ? 0
      : (progress.comboIndex / progress.comboTotal) * 100;
  const comboPct =
    progress.phaseTotalIterations === 0
      ? 0
      : (progress.phaseDone / progress.phaseTotalIterations) * 100;
  const activeOverallPct = progress.isFinished ? 100 : overallPct;
  const phaseDurationMs = progress.phaseStartedAt
    ? now - progress.phaseStartedAt
    : null;
  const totalDurationMs = progress.startedAt ? now - progress.startedAt : null;

  const viewportWidth = measuredWidth ?? rootDimensions?.width ?? 1200;
  const isMobile = viewportWidth < MOBILE_MAX_WIDTH;
  const isTablet = !isMobile && viewportWidth < TABLET_MAX_WIDTH;

  const gridTemplateColumns = isMobile
    ? '1fr'
    : isTablet
    ? '1fr 1fr'
    : '220px 260px 1fr 320px';

  // Items at index 0 (active) and 1 (already next-up) are not clickable.
  // The rest can be promoted to next-up by the user — see daemons/cpc.ts
  // moveBotToNextInQueue.
  const queueItems = queue.queueState?.queue ?? [];
  const queueScrollMaxHeight = isMobile ? 320 : isTablet ? 360 : undefined;

  const renderQueueScroll = () => (
    <div
      className="live-cpc-queue-scroll"
      style={
        queueScrollMaxHeight
          ? { maxHeight: queueScrollMaxHeight }
          : { flex: 1, minHeight: 0 }
      }
    >
      {queueItems.map((q, idx) => (
        <QueueListItem
          key={q.botFriendlyName}
          item={q}
          variant="list"
          onClick={
            idx >= 2 ? () => handleReorder(q.botFriendlyName) : undefined
          }
          rightLabel={idx === 0 ? `${activeOverallPct.toFixed(1)}%` : undefined}
        />
      ))}
    </div>
  );

  // On desktop/tablet the queue sits beside the heatmap; we use the absolute
  // positioning trick so its height is governed by the (taller) heatmap and
  // not by the full queue list. On mobile the queue is its own row, so we
  // give it an explicit max-height to keep it scrollable.
  const queueCell = isMobile ? (
    <Panel title="Queue">{renderQueueScroll()}</Panel>
  ) : (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      <Panel
        title="Queue"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {renderQueueScroll()}
      </Panel>
    </div>
  );

  return (
    <div
      ref={rootRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 16,
        minHeight: '100%',
      }}
    >
      <Panel>
        {progress.isFinished && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: 6,
              background: 'rgba(0, 176, 97, 0.18)',
              border: `1px solid ${colors.green}`,
              color: colors.white,
              fontSize: 14,
              marginBottom: 12,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <span>
              <strong style={{ color: colors.green }}>✓ Run finished</strong>
              {progress.baseAsset && ` — ${progress.baseAsset}-USDC`}
            </span>
            <span style={{ opacity: 0.8 }}>
              took {formatDurationCompact(progress.finishedDuration ?? 0)}
            </span>
          </div>
        )}
        <Bar
          pct={progress.isFinished ? 100 : overallPct}
          label={`Overall · ${progress.comboIndex}/${
            progress.comboTotal
          } combos · ${progress.baseAsset ?? '—'} · ETA ${
            progress.isFinished ? '—' : formatDurationCompact(overallETA ?? 0)
          }`}
        />
        <div style={{ height: 8 }} />
        <Bar
          pct={progress.isFinished ? 100 : comboPct}
          label={`Current combo · ${progress.phaseDone}/${
            progress.phaseTotalIterations
          } · ETA ${
            progress.isFinished ? '—' : formatDurationCompact(comboETA ?? 0)
          }`}
        />
        {progress.interruptedReason && (
          <div style={{ marginTop: 10 }}>
            <InterruptedBanner reason={progress.interruptedReason} />
          </div>
        )}
      </Panel>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns,
          gap: 16,
          alignItems: 'stretch',
        }}
      >
        {queueCell}
        <Panel title="Stats">
          <StatsBlock
            input={{
              baseAsset: progress.baseAsset,
              phase: progress.phase,
              phasePass: progress.phasePass,
              phaseDone: progress.phaseDone,
              phaseTotal: progress.phaseTotalIterations,
              comboIndex: progress.comboIndex,
              comboTotal: progress.comboTotal,
              currentCombo: progress.currentCombo,
              comboETA,
              phaseDurationMs,
              totalDurationMs,
            }}
          />
        </Panel>
        <Panel title="Heatmap">
          {progress.overview ? (
            <ProfitDistributionChart
              data={progress.overview}
              setTimeFrameCombination={() => undefined}
            />
          ) : (
            <div style={{ opacity: 0.6 }}>Waiting for first combo result…</div>
          )}
        </Panel>
        <Panel title="Global best">
          <GlobalBestCard result={progress.bestGlobal} />
        </Panel>
      </div>

      <Panel title="Best result — live SPC">
        {progress.bestGlobal && spcResult ? (
          <SpcResultView key={progress.runId ?? 'no-run'} data={spcResult} />
        ) : (
          <div style={{ opacity: 0.6, padding: 8 }}>
            {progress.bestGlobal
              ? 'Loading SPC for current best…'
              : 'Waiting for first best result…'}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default LiveCPCDaemon;
